import { useContext } from 'react';

import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import { BuildCacheTrial } from '@/models/Account';

import { AddonTrialShowResponse, TrialAddonStatus, TrialAddonType, useAddonTrialShow } from './useAddonTrialShow';

export type BuildCacheAddonTrial = {
  // The trial in the legacy GlobalProps shape the Build Cache UI already consumes, or undefined when
  // there is no trial to represent (never started, needs a card, or already converted/enterprise).
  trial?: BuildCacheTrial;
  status?: TrialAddonStatus;
  isLoading: boolean;
};

const toTrial = (data: AddonTrialShowResponse): BuildCacheTrial | undefined => {
  if (data.status !== TrialAddonStatus.Active && data.status !== TrialAddonStatus.TrialExpired) {
    return undefined;
  }

  // A trial in these states always carries both timestamps; bail rather than hand consumers an
  // empty string that becomes `Invalid Date`/`NaN` in `new Date(...)`.
  if (!data.trialExpiresAt || !data.trialStartedAt) {
    return undefined;
  }

  return {
    isActive: data.status === TrialAddonStatus.Active,
    expiresAt: data.trialExpiresAt,
    startedAt: data.trialStartedAt,
    isDeactivated: false,
  };
};

// Build Cache trial state derived from the subscription-service add-on trial, adapted to the shape the
// existing cards/banners expect. Replaces the metadata-backed account.buildCacheTrial GlobalProp.
export const useBuildCacheAddonTrial = (workspaceSlug: string): BuildCacheAddonTrial => {
  const { account } = useContext(GlobalPropsContext);
  const { data, isLoading } = useAddonTrialShow(workspaceSlug, TrialAddonType.BuildCache, {
    enabled: Boolean(workspaceSlug),
  });

  // Trials started before the add-on system exist only in the metadata GlobalProp, which describes the
  // current account alone. Without the fallback such a workspace looks like it has neither a trial nor a
  // subscription: AccountFeatureService#has_build_cache_subscription? is "entitlement and no active
  // metadata trial", so the metadata trial that the add-on service never learned about turns both off.
  // See ACI-5327.
  const metadataTrial =
    account?.type === 'organization' && account.slug === workspaceSlug ? account.buildCacheTrial : undefined;
  const addonTrial = data ? toTrial(data) : undefined;

  if (!addonTrial && metadataTrial) {
    return {
      trial: metadataTrial,
      status: metadataTrial.isActive ? TrialAddonStatus.Active : TrialAddonStatus.TrialExpired,
      isLoading,
    };
  }

  return {
    trial: addonTrial,
    status: data?.status,
    isLoading,
  };
};
